import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/Card';
import BrowseView, { DEFAULT_BROWSE_STATE } from '../components/dataDictionary/BrowseView';
import OverviewView from '../components/dataDictionary/OverviewView';
import PublishControls, { usePublishStatus } from '../components/dataDictionary/PublishControls';
import useDictionaryMeta from '../components/dataDictionary/useDictionaryMeta';
import { useAuth } from '../contexts/AuthContext';
import { organizationsService } from '../services/organizationsService';

const TABS = [
  ['browse', 'Browse'],
  ['overview', 'Overview'],
];

/**
 * Admin Data Dictionary: curate how consumer-database columns appear in the segment builder.
 * Layout: publish status → Browse | Overview tabs → full-height workspace card.
 * The page title lives in the app top bar.
 */
const DataDictionary = () => {
  const { user } = useAuth();
  const [hasFastCubeAccess, setHasFastCubeAccess] = useState(null);
  const [tab, setTab] = useState('browse');
  const [browseState, setBrowseState] = useState(DEFAULT_BROWSE_STATE);

  useEffect(() => {
    let cancelled = false;
    organizationsService
      .getOrganizations()
      .then((orgs) => !cancelled && setHasFastCubeAccess(organizationsService.hasFastCubeAccess(orgs)))
      .catch(() => !cancelled && setHasFastCubeAccess(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const enabled = hasFastCubeAccess === true;
  const meta = useDictionaryMeta(enabled);
  const publish = usePublishStatus(enabled);

  // After any save, refresh the unpublished count right away and the slower metadata shortly after.
  const metaRefreshTimer = React.useRef(null);
  const handleEdited = useCallback(() => {
    publish.refresh();
    if (metaRefreshTimer.current) clearTimeout(metaRefreshTimer.current);
    metaRefreshTimer.current = setTimeout(() => meta.refresh(), 1500);
  }, [publish, meta]);

  const goBrowse = useCallback((patch) => {
    setBrowseState((prev) => ({ ...prev, ...patch }));
    setTab('browse');
  }, []);

  if (!user?.is_admin) return <Navigate to="/dashboard" replace />;
  if (hasFastCubeAccess === null) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-gray-500">Loading…</p>
        </CardContent>
      </Card>
    );
  }
  if (!hasFastCubeAccess) return <Navigate to="/dashboard" replace />;

  const totalColumns = meta.stats?.overall?.total;

  return (
    <div className="flex flex-col gap-4" style={{ minHeight: 'calc(100vh - 12rem)' }}>
      <div className="flex flex-wrap items-start justify-end gap-4">
        <PublishControls status={publish.status} error={publish.error} onRefresh={publish.refresh} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg bg-[#E8EEF5] p-[3px]" role="tablist" aria-label="Data dictionary views">
          {TABS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              onClick={() => setTab(value)}
              className={`rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors ${
                tab === value ? 'bg-white text-[#18181B] shadow-sm' : 'text-[#71717A] hover:text-[#18181B]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <span className="font-mono text-xs text-[#71717A]">
          bengen-workspace · segment_builder.data_dictionary_overrides
          {totalColumns != null ? ` · ${totalColumns.toLocaleString()} columns` : ''}
        </span>
      </div>

      {meta.error && !meta.stats ? (
        <p className="text-sm text-red-600" role="alert">
          {meta.error}
        </p>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col" style={{ height: 'calc(100vh - 17.5rem)' }}>
        {tab === 'browse' ? (
          <BrowseView state={browseState} onStateChange={setBrowseState} meta={meta} onEdited={handleEdited} publishPending={publish.status?.pending_count || 0} />
        ) : (
          <OverviewView meta={meta} publish={publish} onBrowse={goBrowse} />
        )}
      </div>
    </div>
  );
};

export default DataDictionary;
